import { toInteger, toString, startsWith, toNumber } from 'lodash-es';
import {
  FEEDS,
  BUS_FX_MODE,
  BUS_MODE,
  STREETCAR_MODE,
  RAIL_MODE,
  LIGHT_RAIL_MODE,
  MAX_SHORT_NAME,
  MAX_SHUTTLE_COLOR,
  DEFAULT_ROUTE_COLOR,
  TRIMET_FEED_ID,
  CTRAN_FEED_ID,
  CTRAN_VINE_ROUTE_IDS,
  TRIMET_PSC_AGENCY_ID,
  TRIMET_TRAM_AGENCY_ID,
  AERIAL_TRAM_MODE,
  ROUTE_PROVIDER_CONFIG,
} from '#/shared/constants';
import { getProviderKeyFromRoute } from '#/shared/utils/agency-utils';
import {
  STREETCAR_SUB_TYPE,
  WES_SUB_TYPE,
  MAX_SUB_TYPE,
  SHUTTLE_SUB_TYPE,
  BRT_SUB_TYPE,
  BUS_TYPE,
  AERIAL_TRAM_SUB_TYPE,
} from '#/transit-tracker/constants';
import {
  getShortNameFromRouteNumber,
  isAerialTram,
  isStreetcar,
} from '#/shared/utils/route-name-utils';

export const getColorFromRoute = route =>
  (route.routeSubType === SHUTTLE_SUB_TYPE && MAX_SHUTTLE_COLOR) ||
  (route.routeColor ? `#${route.routeColor}` : DEFAULT_ROUTE_COLOR);

/**
 * TODO: Move the color values stored into the response from the `/vehicles`
 *  service and get rid of this function
 *
 * Get colors for styling vehicles based on the supplied route number
 * @param routeNumber {number} ID for route that vehicle of interest is
 * operating on
 * @return {{routeColor: string, highlightColor: string, badgeColor: string}}
 */
export const routeNumberToVehicleColors = routeNumber => {
  // the otp-ui TransitVehicleOverlay looks colors under these keys on
  // supplied vehicle object and automatically applies them
  const BASE_KEY = 'routeColor';
  const HIGHLIGHT_KEY = 'highlightColor';

  switch (toInteger(routeNumber)) {
    case 90: // MAX Red
      return {
        [BASE_KEY]: '#c4113c',
        [HIGHLIGHT_KEY]: '#ff6b76',
      };
    case 100: // MAX Blue
      return {
        [BASE_KEY]: '#004a98',
        [HIGHLIGHT_KEY]: '#3f9aff',
      };
    case 190: // MAX Yellow
      return {
        [BASE_KEY]: '#fec526',
        [HIGHLIGHT_KEY]: '#ffec6f',
      };
    case 200: // MAX Green
      return {
        [BASE_KEY]: '#00833e',
        [HIGHLIGHT_KEY]: '#5de87a',
      };
    case 290: // MAX Orange
      return {
        [BASE_KEY]: '#d05d15',
        [HIGHLIGHT_KEY]: '#ff9b3a',
      };
    case 203: // WES
      return {
        [BASE_KEY]: '#6e6e6e',
        [HIGHLIGHT_KEY]: '#cccccc',
      };
    case 193: // Streetcar - NS Line
      return {
        [BASE_KEY]: '#8dc63f',
        [HIGHLIGHT_KEY]: '#acf24d',
      };

    case 194: // Streetcar - A Loop
      return {
        [BASE_KEY]: '#d51668',
        [HIGHLIGHT_KEY]: '#ff308a',
      };
    case 195: // Streetcar - B Loop
      return {
        [BASE_KEY]: '#3e0099',
        [HIGHLIGHT_KEY]: '#a15eff',
      };
    case 2: // FX
      return {
        [BASE_KEY]: '#60a511',
        [HIGHLIGHT_KEY]: '#84e80c',
      };
    default:
      return {
        [BASE_KEY]: '#1663b8',
        [HIGHLIGHT_KEY]: '#5cc3ff',
      };
  }
};

/**
 * Returns a short name the supplied route, e.g. 'MAX', 'WES', 'CL', 'A', 'B'
 *
 * @param route {Object}
 * @return {string}
 */
export const getShortNameFromRoute = route => {
  // Handle non-TRIMET routes (always, regardless of feature flag)
  if (route.feedId && route.feedId !== TRIMET_FEED_ID) {
    const result = route.routeShortName || route.id;
    return result;
  }

  // Handle shuttle routes
  if (route.routeSubType === SHUTTLE_SUB_TYPE) {
    return MAX_SHORT_NAME;
  }

  // Default: use routeShortName or fall back to generated name
  // V3 API does not return routeShortName for some Trimet routes
  return route.routeShortName || getShortNameFromRouteNumber(route.id);
};

export const parseRouteVanityName = (id, description) => {
  const idString = `${id.toString()}-`;
  if (description.indexOf(idString) === 0) {
    return description.slice(idString.length);
  }
  return description;
};

export const formatDestinations = directions =>
  // TODO: this will need to be refactored when the `/routeConfig` web
  //  service is localized since its relying on a pattern that will only
  //  be present for English
  directions.map(d => d.desc.replace(/^To\s/, '')).join('/');

/** Derive OTP style mode ID value from route metadata */
export const routeTypeToMode = route => {
  const { routeSubType, type } = route;

  if (routeSubType === BRT_SUB_TYPE) {
    return BUS_FX_MODE;
  }

  if (routeSubType === STREETCAR_SUB_TYPE) {
    return STREETCAR_MODE;
  }

  if (routeSubType === WES_SUB_TYPE) {
    return RAIL_MODE;
  }

  if (routeSubType === MAX_SUB_TYPE) {
    return LIGHT_RAIL_MODE;
  }

  if (routeSubType === AERIAL_TRAM_SUB_TYPE) {
    return AERIAL_TRAM_MODE;
  }

  if (type === BUS_TYPE) {
    return BUS_MODE;
  }

  return null;
};

export const mapRoute = r => {
  const routeColor = getColorFromRoute(r);
  const isShuttle = r.routeSubType === SHUTTLE_SUB_TYPE;
  const identifier = r.routeShortName || r.id;
  const description = r.routeLongName || r.desc;

  return r
    ? {
        ...r,
        routeColor,
        routeGeometryColor: isShuttle ? DEFAULT_ROUTE_COLOR : routeColor,
        shortName: getShortNameFromRoute(r),
        vanityName: parseRouteVanityName(identifier, description),
        isShuttle,
      }
    : null;
};

export const countAlertsByRoute = alerts => {
  const groupedAlerts = {};
  alerts
    .filter(a => a.route)
    .forEach(a => {
      a.route.forEach(r => {
        if (!groupedAlerts[r.id]) {
          groupedAlerts[r.id] = 0;
        }
        groupedAlerts[r.id] += 1;
      });
    });
  return groupedAlerts;
};

/**
 * For use with the lookup returned by `useGetRouteBasicsQuery`
 */
export const routeLookupToSortedList = routeLookup =>
  (routeLookup ? Object.values(routeLookup) : []).sort(
    (a, b) => a.routeSortOrder - b.routeSortOrder
  );

/**
 * Filter routes by provider according to configuration
 * @param {Array} routes - All available routes
 * @param {Object} selectedProvider - The provider to filter by
 * @returns {Array} - Filtered routes for the selected provider
 */
export const filterRoutesByProvider = (routes, selectedProvider) => {
  if (!routes.length || !selectedProvider) {
    return routes;
  }

  if (selectedProvider.feedId === TRIMET_FEED_ID) {
    if (selectedProvider.agencyId === TRIMET_PSC_AGENCY_ID) {
      // Streetcar routes
      return routes.filter(route => isStreetcar(route.feedId, route.id));
    }
    if (selectedProvider.agencyId === TRIMET_TRAM_AGENCY_ID) {
      // Aerial Tram route
      return routes.filter(route => isAerialTram(route.feedId, route.id));
    }
    // Main TriMet routes - exclude sub-agency routes (streetcar and tram)
    return routes.filter(
      route =>
        route.feedId === 'TRIMET' &&
        !isStreetcar(route.feedId, route.id) &&
        !isAerialTram(route.feedId, route.id)
    );
  }
  // External providers
  return routes.filter(route => route.feedId === selectedProvider.feedId);
};

/**
 * Sort routes within a provider according to requirements:
 * - TriMet (including sub-agencies): preserve existing sort (MAX, WES, then bus lines numerically)
 * - Other providers: sort alphabetically by route_short_name then route_long_name or route_id
 */
export const sortRoutesWithinProvider = (routes, provider) => {
  if (!routes || routes.length === 0) return routes;

  // For TriMet and TriMet sub-agencies (Streetcar, Aerial Tram), preserve existing sorting
  if (provider.feedId === TRIMET_FEED_ID) {
    return routes.sort((a, b) => {
      // Use routeSortOrder if available, otherwise fall back to ID comparison
      const sortOrderA =
        a.routeSortOrder !== undefined ? a.routeSortOrder : Number(a.id) || 0;
      const sortOrderB =
        b.routeSortOrder !== undefined ? b.routeSortOrder : Number(b.id) || 0;
      return sortOrderA - sortOrderB;
    });
  }

  // For all other providers, sort alphabetically
  return routes.sort((a, b) => {
    // Special handling for C-TRAN: prioritize The Vine routes
    if (provider.feedId === CTRAN_FEED_ID) {
      const aIsVine = CTRAN_VINE_ROUTE_IDS.includes(a.id);
      const bIsVine = CTRAN_VINE_ROUTE_IDS.includes(b.id);

      if (aIsVine && !bIsVine) return -1;
      if (!aIsVine && bIsVine) return 1;
    }

    const aShortName = a.routeShortName || a.shortName || '';
    const bShortName = b.routeShortName || b.shortName || '';

    // First compare by short name
    const shortNameComparison = aShortName.localeCompare(
      bShortName,
      undefined,
      {
        numeric: true,
        sensitivity: 'base',
      }
    );

    if (shortNameComparison !== 0) {
      return shortNameComparison;
    }

    // If short names are equal, compare by long name
    const aLongName = a.routeLongName || a.vanityName || '';
    const bLongName = b.routeLongName || b.vanityName || '';
    const longNameComparison = aLongName.localeCompare(bLongName, undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    if (longNameComparison !== 0) {
      return longNameComparison;
    }

    // Finally, compare by route ID
    return String(a.id || '').localeCompare(String(b.id || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

// Function to group routes by provider
export const groupRoutesByProvider = routes => {
  const groups = {};

  routes.forEach(route => {
    const providerKey = getProviderKeyFromRoute(route);

    if (!groups[providerKey]) {
      groups[providerKey] = [];
    }

    groups[providerKey].push(route);
  });

  // Sort routes within each group
  Object.keys(groups).forEach(providerKey => {
    const provider = ROUTE_PROVIDER_CONFIG.find(
      p => (p.agencyId || p.feedId) === providerKey
    );
    if (provider) {
      groups[providerKey] = sortRoutesWithinProvider(
        groups[providerKey],
        provider
      );
    }
  });

  return groups;
};
/**
 * Normalize a route short name by removing leading zeros, trimming whitespace,
 * and preserving alphanumeric characters.
 *
 * @param {string|number} num - The route number to normalize
 * @returns {string} The normalized route short name
 */
export const normalizeRouteShortNameFromRouteNumber = num => {
  // Coerce to string, trim, drop leading zeros, preserve alphanumerics
  const str = toString(num).trim();
  return startsWith(str, '0') && toNumber(str) ? toString(toNumber(str)) : str;
};

/**
 * Based on feedId, use routeNumber to derive routeShortName
 * If feedId is 'TRIMET', leverage classic mode of getting shortName via
 * getShortNameFromRouteNumber from shared/utils/route-name-utils
 * otherwise, use normalizeRouteShortNameFromRouteNumber to strip leading zeros
 * to preserve non-Trimet agency presentational standards (i.e. CTRAN doesn't have 030,
 * but does have a route 30).
 * @param { string } feedId - the feed identification string
 * @param { string | number } routeNumber - the route number from the record being passed
 * through convertRecord
 * @returns { string } the derived route short name
 */
export const deriveRouteShortName = (feedId, routeNumber) => {
  return feedId === FEEDS.TRIMET
    ? getShortNameFromRouteNumber(Number(routeNumber))
    : normalizeRouteShortNameFromRouteNumber(routeNumber);
};
